// Centralized API client for LexCRM backend.
// Uses Vite dev-server proxy (see vite.config.js) so requests to /api are forwarded to :4000.

import { srvErrText } from "./lib/srvErr";
// 81 §4 — the device id we issue, attached to every request. See lib/deviceId.ts
// for why it is a header rather than a body field, and why it survives logout.
import { deviceHeader, DEVICE_KEY } from "./lib/deviceId";
// 82 §4 — TYPE-ONLY, so this carries no runtime dependency on a browser-only
// module: the ceremony options are shaped by the file that decodes them.
import type { CreationOptionsJSON, RequestOptionsJSON } from "./lib/webauthnClient";
// 182 A.2 — the contact timeline's row, from the generated contract.
import type { Interaction } from "@shared/entities";

export const API_URL = "/api";
const TOKEN_KEY = "lx:token";
const USER_KEY = "lx:currentUser"; // legacy — retained for backward compat, NOT a reliable auth source
// lx:authUser is the SINGLE source of truth for the signed-in identity. It
// is written only by login() (password) and by main.jsx's SSO handler, and
// cleared on logout / 401. The "Viewing as" simulation writes lx:cu and
// never touches this, so the real admin identity is always recoverable.
const AUTH_USER_KEY = "lx:authUser";

// ── 05s — WHAT SIGNING OUT ACTUALLY REMOVES ──────────────────────────────────
//
// It used to be three keys: the token, and the two identity mirrors. The census
// (docs/data-flow-inventory.md §5.3, #253) counted ~79 `lx:` literals in src/
// and found the gap: the ENTITY CACHES — `lx:cl` is the whole client table,
// names, emails, addresses, nationalRegister — are no longer WRITTEN by the
// sync engine, but App.tsx:3768 still READS them at boot as an offline
// fallback. A browser that last synced under the write-through era still holds
// that snapshot, nothing refreshes it and nothing expired it. So signing out of
// a law firm's application on a borrowed machine left the firm's client list
// behind.
//
// THE RULE IS A PREFIX, NOT A LIST, and that is the whole point. An enumeration
// of ~79 keys is a list that is wrong the first time somebody adds the 80th —
// and this codebase has the receipt: `doLogout` in App.tsx had already grown a
// hand-written fourth `removeItem("lx:cu")` at the call site, because the list
// in here did not cover it. Everything this application writes shares one
// prefix; sweeping the prefix covers the key nobody has written yet.
const APP_KEY_PREFIX = "lx:";

// The exceptions, and each one owes a reason. There is exactly ONE.
//
// `lx:lang` is read by I18nProvider BEFORE anybody is authenticated
// (src/i18n.tsx:78-86) — it is how the LOGIN SCREEN knows to render in French
// or Dutch. Clearing it would send a Belgian firm back to English on every sign
// out. It is also not personal data in any useful sense: one of three language
// codes, and the login page is about to display that language anyway.
//
// Everything else that looks durable — theme, font, text size, agenda filters,
// sort orders — is NOT kept, deliberately, because it is not lost: those keys
// are ingested into Collaborator.preferences at login and cleared locally
// (src/lib/ingestLegacyPrefs.ts), so the server hands them back at the next
// sign-in. Keeping a local copy would preserve one user's settings for the next
// person to use the machine, which is the opposite of what it looks like.
//
// So the promise this makes is exact: AFTER LOGOUT THIS BROWSER HOLDS NO `lx:`
// KEY EXCEPT `lx:lang`.
// 81 §4 — `lx:device` is the SECOND exception, and it owes its reason here.
//
// It is 128 random bits with no input at all: nothing about the machine, the
// person, the network or the session goes into it (src/lib/deviceId.ts). A
// device id wiped at sign-out is not a device id — every sign-in would arrive
// as a new browser, the attempt log could never say "the same one as last
// time", and D.8's trusted devices would have nothing to trust. The sweep
// exists because a borrowed machine kept the FIRM'S CLIENT TABLE; keeping a
// number that says nothing about anybody is not that, and clearing browser data
// still removes it, which is what §4 promises and what the screens say.
//
// So the promise is now exact in two parts: AFTER LOGOUT THIS BROWSER HOLDS NO
// `lx:` KEY EXCEPT `lx:lang` AND `lx:device`.
// BOTH THE LITERAL AND THE CONSTANT, and that is not a slip. The literal is
// what `scripts/lint/logout-clears-app-storage.js` can READ — it parses this
// line's source text on purpose, so an exception added without a reason shows in
// the diff, and an identifier here would make the exception invisible to it. The
// constant is what keeps the two from drifting: `Set` dedupes them while they
// agree, and the day lib/deviceId.ts renames its key they stop agreeing — the
// sweep keeps both, and the lint reports the new key as an undocumented
// survivor. The alternative, a lone literal, would quietly start wiping the
// device id at every sign-out with nothing failing anywhere.
const LOGOUT_KEEP: ReadonlySet<string> = new Set(["lx:lang", "lx:device", DEVICE_KEY]);

/**
 * Remove every key this application owns, except the documented exceptions.
 *
 * Collect-then-delete: `localStorage.key(i)` re-indexes on every removal, so
 * deleting inside the walk silently skips its neighbour — which would leave
 * roughly half the keys behind while looking like it had cleared them all.
 */
export function clearAppStorage() {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(APP_KEY_PREFIX) && !LOGOUT_KEEP.has(k)) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {}
  // sessionStorage carries one key (`lx:chunkReload`, the reload-loop guard in
  // main.tsx) and it is swept on the same rule rather than named, for the same
  // reason the localStorage sweep is a prefix.
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(APP_KEY_PREFIX) && !LOGOUT_KEEP.has(k)) doomed.push(k);
    }
    for (const k of doomed) sessionStorage.removeItem(k);
  } catch {}
}

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
export const setToken = (t: any) => {
  try {
    t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);
  } catch {}
};
export const getCurrentUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
export const setCurrentUser = (u: any) => {
  try {
    u
      ? localStorage.setItem(USER_KEY, JSON.stringify(u))
      : localStorage.removeItem(USER_KEY);
  } catch {}
};
export const getAuthUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};
export const setAuthUser = (u: any) => {
  try {
    if (u && u.id != null) {
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({
          id: u.id,
          email: u.email || "",
          role: u.role || "",
          isAdmin: !!u.isAdmin,
        }),
      );
    } else {
      localStorage.removeItem(AUTH_USER_KEY);
    }
  } catch {}
};

// ── BRIEF 163 §B.3 — THE SEAM, TYPED ────────────────────────────────────────────────
//
// This line had `endpoint: any, options: any` and an untyped res.json(), with
// fan-in 83 — 35,905 of the linter's day-one findings flowed from it. Now:
//   · `endpoint` is a string; the server's full inventory is the generated
//     GeneratedRouteKey union (src/apiRoutes.ts — regenerate with
//     node scripts/gen-api-routes.js; the cockpit's /api/fleet/* lands there).
//   · options is the RequestInit subset actually used, typed.
//   · the return is Promise<T> with T = unknown BY DEFAULT — never any: a
//     value nobody named cannot be summed into a bill without the compiler
//     asking what it is. Sites name shapes with `api<Shape>(…)` or a cast.
//   · KnownRoutes maps "VERB /path" keys to named response shapes; the typed
//     helpers below (api.get/post/…) resolve it automatically for literal
//     endpoints, and it wins over the generated inventory as shapes get named.
export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: string | FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

/** Named response shapes, keyed "VERB /path". Grows as shapes get names. */
export interface KnownRoutes {
  "GET /version": {
    buildId: string | null;
    servedBuildId: string | null;
    apiStale: boolean;
    serverId: string | null;
    servedServerId: string | null;
    serverStale: boolean;
    bundleStale: boolean | null;
    isCockpit: boolean;
    frontendSourceId: string | null;
    bundleSourceId: string | null;
    startedAt: string | number | null;
  };
  "GET /auth/session": SessionResponse;
  // 182 A.2 — the one literal interaction endpoint; the three parameterised
  // ones (a query string, an id) are cast at their helpers below.
  "POST /interactions": InteractionRow;
}

/** Per-endpoint return type: KnownRoutes wins; everything else is unknown. */
export type RouteReturn<K extends string> = K extends keyof KnownRoutes
  ? KnownRoutes[K]
  : unknown;

/**
 * 174 §1.2 — THE HEADERS EVERY AUTHENTICATED CALL SENDS, IN ONE PLACE.
 *
 * THE BROWSER MAY HOLD TWO CREDENTIALS, not one: the Bearer token in
 * localStorage (`lx:token`, read by getToken above) and the httpOnly
 * `lx_session` cookie the server sets (server/src/lib/authSession.js:90-99).
 * `middleware/auth.js:24-35` accepts EITHER — Bearer wins, the cookie is the
 * fallback, neither is a 401 `auth.missing_token`.
 *
 * WHICH ONE A GIVEN BROWSER HOLDS, precisely, because the brief's "the browser
 * holds BOTH" is true of the browsers that hit the defect and not of a fresh
 * login. MEASURED on this tree: NOTHING WRITES `lx:token` any more. The three
 * `setToken` call sites all CLEAR it (`src/api.ts:284` the 401 branch and
 * `src/api.ts:898` logout, both `setToken(null)`),
 * and `adoptSession` stopped storing it at 167 §2b. So a session opened today
 * carries the cookie alone; a session opened BEFORE that deploy still carries
 * the Bearer, and 174 §1.1 mints it a cookie on its first call through here.
 * Sending both is what makes the window between those two states work.
 *
 * So every authenticated call SENDS BOTH and lets the server take whichever
 * arrives. A call site that deliberately sends only one is a defect: 167 §2d
 * left sixteen bare fetches carrying a comment that said "no Bearer to send
 * (none in the browser)", which was never true — this file has read `lx:token`
 * the whole time — and a session that kept localStorage while losing the
 * cookie saw every one of them answer 401 while api() worked perfectly. That
 * is what locked a second collaborator out of every SharePoint button.
 *
 * Named here rather than copied because the two places api() cannot reach — a
 * blob download and a multipart upload, which need their own `fetch` — must
 * send exactly what api() sends, and a second hand-written copy is what drifts.
 * `scripts/lint/one-door-fetch.js` is the net that keeps that true.
 *
 * The cookie needs no header: it rides `credentials: "same-origin"`.
 */
export function authHeaders(): Record<string, string> {
  const token = getToken();
  return {
    // 167 §2d — the SameSite=Lax CSRF answer, on EVERY call. A bare cross-site
    // form POST cannot set a custom header, so the server refuses a cookie-auth
    // mutation without it (middleware/auth.js). Harmless on a GET and on a
    // Bearer call.
    "X-LexCRM": "1",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // 81 §4 — on EVERY request, not only the authenticating ones. A browser
    // with no id (storage disabled) spreads nothing rather than an empty
    // header, and a route that starts caring later needs no client change.
    ...deviceHeader(),
  };
}

export async function api<T = unknown>(
  endpoint: string,
  options: ApiOptions = {},
): Promise<T> {
  const headers = {
    "Content-Type": "application/json",
    // Present before the caller's own headers so none of the three can be
    // dropped by an options.headers that omits it.
    ...authHeaders(),
    ...(options.headers || {}),
  };

  // 167 §2d — send the httpOnly lx_session cookie on same-origin calls (the app
  // and /api share an origin). This is fetch's default, stated for intent and
  // so a future default change cannot silently log everyone out.
  const res = await fetch(API_URL + endpoint, { ...options, credentials: "same-origin", headers });

  if (res.status === 401) {
    // 167 §2d — the cookie dies WITH the storage. Fire-and-forget through
    // api.post (which adds credentials + X-LexCRM). The brief asked for a raw
    // fetch "never api(), which would recurse through this handler" — but that
    // recursion can only happen if the call itself 401s, and /auth/logout is a
    // PUBLIC route that always answers 200, so api.post cannot re-enter this
    // branch. Using api() keeps the one fetch call and adds no lint suppression.
    void api.post("/auth/logout").catch(() => {
      /* a logout that cannot be sent still clears the storage below */
    });
    setToken(null);
    setCurrentUser(null);
    setAuthUser(null);
    // 05s — an expired session ends the same way a deliberate sign-out does.
    // This is the path a shared machine actually takes: nobody clicks Sign out,
    // they walk away and the 7-day token lapses. If only the explicit logout
    // swept, the case that leaves a browser unattended would be the one case
    // that kept the client list.
    clearAppStorage();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("lx:unauthorized"));
    }
    throw new Error("Unauthorized");
  }

  if (res.status === 204) return null as T;

  const ct = res.headers.get("content-type") || "";
  const body: unknown = ct.includes("application/json")
    ? await res.json()
    : await res.text();
  const envelope = (body && typeof body === "object" ? body : {}) as {
    error?: string;
    code?: string;
  };

  if (!res.ok) {
    // brief 38 §3 — the `srvErr.<code>` lookup, at the one seam where an
    // envelope becomes a client-visible string. Returns null unless the seeded
    // row IS this message in another language (or the message slot carries an
    // identifier), so the server's own words survive wherever they say more
    // than the code's label does. `err.body.error` keeps the wire text either
    // way; nothing in src/ compares `e.message` against English (checked).
    const msg =
      srvErrText(body) ||
      envelope.error ||
      res.statusText ||
      "Request failed";
    // Phase M+3.7 — preserve structured error envelope on the thrown Error
    // so call sites can branch on a stable `code` instead of substring-
    // matching English text. Existing `e.message` consumers stay
    // unaffected.
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = body;
    err.code = typeof envelope.code === "string" ? envelope.code : null;
    throw err;
  }
  return body as T;
}

// Typed helpers: a LITERAL endpoint resolves its shape through KnownRoutes.
api.get = <E extends string>(ep: E) => api<RouteReturn<`GET ${E}`>>(ep);
api.post = <E extends string>(ep: E, data?: unknown) =>
  api<RouteReturn<`POST ${E}`>>(ep, { method: "POST", body: JSON.stringify(data ?? {}) });
api.put = <E extends string>(ep: E, data?: unknown) =>
  api<RouteReturn<`PUT ${E}`>>(ep, { method: "PUT", body: JSON.stringify(data ?? {}) });
api.patch = <E extends string>(ep: E, data?: unknown) =>
  api<RouteReturn<`PATCH ${E}`>>(ep, { method: "PATCH", body: JSON.stringify(data ?? {}) });
api.del = <E extends string>(ep: E, data?: unknown) =>
  api<RouteReturn<`DELETE ${E}`>>(ep, {
    method: "DELETE",
    ...(data !== undefined ? { body: JSON.stringify(data) } : {}),
  });

// Auth
//
// 78 §1 — THE INVARIANT, ON THIS SIDE OF THE WIRE. A login that needs a second
// step answers with NO token, and `adoptSession` is the only thing here that
// writes one. So a caller who ignores the second step stores nothing: there is
// no branch on which a `mfaRequired`/`enrolmentRequired` response leaves this
// browser holding a credential.
//
// `setToken(undefined)` would already have removed rather than stored (setToken
// treats a falsy value as a clear), so the old shape was not a leak — but it
// also cleared the CURRENT session on a response that was not a refusal, and
// "harmless by accident" is not the property this brief needs. The write is
// explicit instead.
// 78 §1 — the shapes the auth endpoints answer with. Written rather than
// `any` because the RATCHET is right: a new `any` gets a type, not a new
// baseline. `token` is OPTIONAL on purpose — a login that owes a second step
// answers without one, and that optionality IS the invariant, in the type.
export interface AuthUser {
  id: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  isAdmin?: boolean;
}
export interface SessionResponse {
  token?: string;
  user?: AuthUser;
}
export interface RecoveryCount {
  remaining: number;
  total: number;
  /**
   * 82a's glance — when the CURRENT set was issued. A set is replaced outright
   * whenever another is issued, so a printed sheet is either the live one or
   * ten dead codes, and a date is what lets somebody tell which by looking at
   * the paper in their hand. Null when no codes exist.
   */
  issuedAt?: string | null;
}
export interface LoginResponse extends SessionResponse {
  /** A second step is owed and this response carries NO token. */
  mfaRequired?: boolean;
  /** The tenant requires a step this person has not enrolled (AUTH-7). */
  enrolmentRequired?: boolean;
  shapes?: string[];
  challenge?: string;
  recoveryCodes?: RecoveryCount;
  /**
   * 82 §4 — the options the browser hands the authenticator, present only when
   * the person holds a passkey the tenant still permits. A passkey cannot be
   * typed, so they travel WITH the challenge rather than being fetched after.
   */
  webauthn?: RequestOptionsJSON;
  /**
   * 83 §5 (glance) — what to pre-fill the "name this browser" box with, computed
   * server-side from this request's own user-agent. Carried here rather than
   * derived on the client, so there is no second implementation of `labelFor`
   * to drift from the one that actually writes the row.
   */
  suggestedDeviceName?: string;
}
export interface QrMatrix {
  size: number;
  rows: string[];
}
/** 82 §4 — what `/auth-factors/webauthn/start` answers. */
export interface PasskeyStartResponse {
  ticket: string;
  options: CreationOptionsJSON;
}
export interface EnrolStartResponse {
  ticket: string;
  secret: string;
  uri: string;
  qr: QrMatrix;
  issuer: string;
  account: string;
}
export interface EnrolConfirmRequest {
  challenge?: string;
  ticket: string;
  code: string;
  label?: string;
}
export interface EnrolConfirmResponse extends SessionResponse {
  factor?: unknown;
  /** The ONLY time the ten codes cross the wire. */
  recoveryCodes?: string[];
  counts?: RecoveryCount;
  /**
   * 82a's glance — how many recovery codes this call DESTROYED. Zero on a first
   * enrolment; ten on any later one, because a set is replaced outright. The
   * codes screen says so only when it is non-zero, so it can never claim a
   * printed sheet died that never existed.
   */
  replaced?: number;
}
/**
 * One row as `shapeAuthFactor` hands it out — FACTOR_COLUMNS minus the secret,
 * which is never selected and never shaped (server/src/lib/shapes.js).
 * `recovery_code` rows are in here too; a screen listing login factors filters
 * them out, because ten codes are one fact and not ten rows to a reader.
 */
export interface AuthFactorRow {
  id: number;
  collaboratorId: number;
  type: string;
  label?: string | null;
  expiresAt?: string | null;
  usedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}
/** `GET /api/auth-factors` — this person's own, and never another's. */
export interface AuthFactorsResponse {
  factors: AuthFactorRow[];
  /** What the tenant permits, after the floor (authPolicy.resolve). */
  permitted: string[];
  /** What he actually holds, after the tenant. */
  enrolled: string[];
  /** 'off' | 'optional' | 'required' — the tenant's second-step posture. */
  mode: string;
  secondStepRequired: boolean;
  /** A COUNT of the ten, never the codes. */
  recoveryCodes: RecoveryCount;
  /**
   * 83 (glance) — whether a password still exists on this account. It decides
   * whether the recovery sheet can be spent AT ALL: a code is only ever typed at
   * the second step, and the second step is only reached through a password.
   */
  hasPassword?: boolean;
}
export interface RegenerateResponse {
  /** Shown once, exactly as at enrolment. */
  recoveryCodes: string[];
  counts: RecoveryCount;
  /** 82a's glance — the previous set this call destroyed. Always 10 from here. */
  replaced?: number;
}
/**
 * `GET /api/auth-factors/of/:id` — the admin read (80 §3). The same body as
 * above plus who it is about, so a screen listing several people cannot lose
 * track of whose rows it is holding.
 */
export interface AuthFactorsOfResponse extends AuthFactorsResponse {
  collaborator: {
    id: number;
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
}

function adoptSession<T extends SessionResponse>(res: T): T {
  // 167 §2d — a session opened HERE lives in the httpOnly lx_session cookie the
  // server set on this very response; this stores no Bearer. That is not the
  // same as "the browser has none" — a session that predates 167 §2b still
  // holds `lx:token`, which is why authHeaders() sends it when it is there
  // (174 §1.2). The guard stays: a second-step response carries no token, so
  // this still stores nothing on that branch. We keep only the IDENTITY hints
  // (never a secret): lx:currentUser for display, lx:authUser for the admin
  // switcher gate.
  if (!res || typeof res.token !== "string" || !res.token) return res;
  setCurrentUser(res.user);
  setAuthUser(res.user);
  return res;
}

/** 83 §2 — what `/auth/passwordless/start` answers. No subject: nobody is named. */
export interface PasswordlessStartResponse {
  challenge: string;
  webauthn: {
    challenge: string;
    rpId: string;
    userVerification?: string;
    allowCredentials?: { type: "public-key"; id: string; transports?: string[] }[];
    timeout?: number;
  };
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return adoptSession((await api.post("/auth/login", { email, password })) as LoginResponse);
}

/** 78 §1 — spend the challenge: six digits, or one of the ten recovery codes. */
export async function secondStep(
  challenge: string,
  code: string,
  trustDevice = false,
  deviceName = "",
): Promise<SessionResponse> {
  return adoptSession((await api.post("/auth/second-step", { challenge, code, trustDevice, deviceName })) as SessionResponse);
}

// 78 §1 — enrolment. Both calls take an OPTIONAL `challenge`: with one they are
// the forced-enrolment path (AUTH-7, no session exists yet); without one they
// are the ordinary path from Settings, authenticated by the Bearer token `api`
// already attaches.
export const enrolTotpStart = (challenge?: string): Promise<EnrolStartResponse> =>
  api.post("/auth-factors/totp/start", challenge ? { challenge } : {}) as Promise<EnrolStartResponse>;

export async function enrolTotpConfirm(opts: EnrolConfirmRequest): Promise<EnrolConfirmResponse> {
  // Confirming through a challenge mints the session (the person has now proved
  // a password and the factor), so this adopts one when it is there.
  return adoptSession((await api.post("/auth-factors/totp/confirm", opts)) as EnrolConfirmResponse);
}

// 82 §4 — the passkey pair, the same two calls with the same optional
// `challenge` and for the same reason. What differs is that neither carries a
// typed value: `start` answers the options the authenticator needs and
// `confirm` posts back what it signed.
// 83 §2 — `passwordless` asks the server for a DISCOVERABLE credential (shape
// (c)) rather than an ordinary second-factor one. Same route, same ceremony,
// same row type: what changes is `authenticatorSelection`, because a
// passwordless sign-in names nobody and the authenticator must be able to
// produce the credential on its own.
export const enrolPasskeyStart = (
  challenge?: string,
  passwordless = false,
): Promise<PasskeyStartResponse> =>
  api.post("/auth-factors/webauthn/start", {
    ...(challenge ? { challenge } : {}),
    ...(passwordless ? { passwordless: true } : {}),
  }) as Promise<PasskeyStartResponse>;

export async function enrolPasskeyConfirm(opts: {
  challenge?: string;
  ticket: string;
  registration: unknown;
  label?: string;
  /** 83 — the authenticator's own `credProps.rk`, where the browser reports it. */
  credProps?: { rk?: boolean };
}): Promise<EnrolConfirmResponse> {
  return adoptSession((await api.post("/auth-factors/webauthn/confirm", opts)) as EnrolConfirmResponse);
}

// ── 83 §2 — SHAPE (c): THE PASSKEY ALONE ────────────────────────────────────
//
// The one pair on this client that sends NO IDENTIFIER of any kind. `start`
// takes no arguments because there is nothing to name — the tenant comes from
// the Host and the account is discovered from whatever credential the
// authenticator offers, which is the inversion shape (c) is.
//
// `adoptSession` on the finish, like every other door that can mint one, so a
// passwordless sign-in stores its token through the same path as a password
// sign-in and cannot drift from it.
/**
 * 83 §3 — SPEND AN ENROLMENT INVITATION. Answers the `enrol` ticket this
 * application already has, never a session: the person still has to enrol.
 *
 * NOT called on page load. Opening the link must not spend the invitation — a
 * mail scanner or a link-preview bot follows URLs, and a GET that spent one
 * would burn it before the person ever saw the screen. It is a POST behind a
 * button the person presses.
 */
export interface InvitationAcceptResponse {
  enrolmentRequired: boolean;
  shapes: string[];
  challenge: string;
  witness: string;
  approval: {
    identity: "none" | "known";
    provisionedAt: string | null;
    issuedAt: string | null;
    address: string | null;
    country: string | null;
    city: string | null;
    deviceId: string | null;
    acceptedAt: string | null;
  };
}

export const acceptInvitation = (token: string): Promise<InvitationAcceptResponse> =>
  api.post("/auth/invitation/accept", { token }) as Promise<InvitationAcceptResponse>;

export const passwordlessStart = (): Promise<PasswordlessStartResponse> =>
  api.post("/auth/passwordless/start", {}) as Promise<PasswordlessStartResponse>;

export async function passwordlessFinish(
  challenge: string,
  assertion: unknown,
): Promise<SessionResponse> {
  return adoptSession((await api.post("/auth/passwordless/finish", { challenge, assertion })) as SessionResponse);
}

// ── 83 §5 (AUTH-14) — TRUSTED DEVICES ───────────────────────────────────────
//
// The device id itself never appears in these signatures: it rides on the
// `X-LX-Device` header `api` already attaches to EVERY request (81 §4), so the
// server reads which browser is asking without any route agreeing on a field
// name. `trustDevice` is a request to REMEMBER this one, spent at the moment a
// second step is passed and nowhere else.
export interface TrustedDevice {
  id: number;
  label: string | null;
  trustedAt: string;
  lastUsedAt: string | null;
  expiresAt: string;
  /** 83 §5 — roughly where the trust was given. An INDICATION; null = not known. */
  country: string | null;
  city: string | null;
  /** Whether this is the browser the person is looking at. Resolved server-side. */
  current: boolean;
}

export interface TrustedDevicesResponse {
  days: number;
  devices: TrustedDevice[];
}

export const listTrustedDevices = (): Promise<TrustedDevicesResponse> =>
  api.get("/auth-factors/trusted-devices") as Promise<TrustedDevicesResponse>;

export const renameTrustedDevice = (id: number, label: string) =>
  api.patch(`/auth-factors/trusted-devices/${id}`, { label });

/**
 * BRIEF 98 §5 — the `AuthFactor.type` this route accepts, named once.
 *
 * `DELETE /auth-factors/trusted-devices/:id` refuses a row of any other type
 * with a 404 (authFactors.js:818), deliberately: "letting it delete a passkey
 * because somebody passed its id would be a second, unguarded factor-removal
 * door." So a screen offering that verb per row has to know which rows it
 * applies to, and the string belongs here beside the call rather than inline in
 * a component. It mirrors `trustedDevices.js:57`.
 */
export const TRUSTED_DEVICE_TYPE = "trusted_device";
export const removeTrustedDevice = (id: number) =>
  api.del(`/auth-factors/trusted-devices/${id}`);

/** §5's "removable in bulk", and an admin may name somebody else. */
export const removeAllTrustedDevices = (collaboratorId?: number) =>
  api.del(`/auth-factors/trusted-devices${collaboratorId ? `?collaboratorId=${collaboratorId}` : ""}`);

/** 82 §4 — spend the challenge with an assertion instead of six digits. */
export async function secondStepPasskey(
  challenge: string,
  assertion: unknown,
  trustDevice = false,
  deviceName = "",
): Promise<SessionResponse> {
  return adoptSession((await api.post("/auth/second-step", { challenge, assertion, trustDevice, deviceName })) as SessionResponse);
}

export const listAuthFactors = (): Promise<AuthFactorsResponse> =>
  api.get("/auth-factors") as Promise<AuthFactorsResponse>;
/** Admin-gated; answers 404 (never 403) for anyone else's rows. */
export const listAuthFactorsOf = (
  collaboratorId: number,
): Promise<AuthFactorsOfResponse> =>
  api.get(`/auth-factors/of/${collaboratorId}`) as Promise<AuthFactorsOfResponse>;
export const deleteAuthFactor = (id: number) => api.del(`/auth-factors/${id}`);

/**
 * BRIEF 96 §3a — `POST /api/auth-factors/invitations/:collaboratorId`.
 *
 * THE SENDING HALF, WHICH HAD NO CLIENT AT ALL. The route has minted enrolment
 * invitations since brief 83 §3 and `acceptInvitation` above has spent them
 * since, but nothing in `src/` ever asked for one — which is why a token had to
 * be pasted into a browser to reach `InvitationView` at all.
 *
 * THE ROUTE SENDS NOTHING. It answers the token to its caller, exactly once
 * (`authFactors.js:676` — every later read answers a row holding a hash), so
 * the caller is expected to be a screen that shows an administrator a link to
 * hand over. Re-issuing REPLACES any outstanding invitation for that person
 * rather than adding a second way in.
 *
 * `hours` and `witness` come back because neither is the caller's to know: both
 * are settings rows (`authInvitationHours`, `authInvitationWitness`), defaulted
 * in `server/src/lib/enrolmentInvitation.js` to 24 hours and `in_app`.
 *
 * BRIEF 103 §1 — IT WAS `days` UNTIL TODAY. 83 §3(1) ruled the window in days
 * on the premise of a link delivered by email; since 96 nothing is mailed and
 * the token is handed to an administrator's screen, so the ruling is overturned
 * and the unit with it. A firm that only ever wrote `authInvitationDays` still
 * gets what it chose — the server converts it — but the wire says hours,
 * because §2d's sentence has to be true for a window shorter than a day.
 */
export interface EnrolmentInvitationResponse {
  /** The only time it ever crosses. Do not store it. */
  token: string;
  /** The window this firm's setting gave it, in HOURS (103 §1). */
  hours: number;
  /** 83 §3(2) — `in_app` | `channel` | `approval` | `phrase`. */
  witness: string;
  expiresAt: string;
}
export const sendEnrolmentInvitation = (
  collaboratorId: number,
): Promise<EnrolmentInvitationResponse> =>
  api.post(`/auth-factors/invitations/${collaboratorId}`) as Promise<EnrolmentInvitationResponse>;

/**
 * BRIEF 103 §2 — THE INVITATION POLICY, WHICH HAD NO SCREEN.
 *
 * Two settings rows, admin-only on the route since 83 §3(4)
 * (`server/src/routes/settings.js:48`) and unreachable from anywhere in `src/`
 * until now — which is the whole of §2's complaint: they were split from each
 * other only because the screen was never built.
 *
 * The KEY NAMES LIVE HERE AND NOWHERE ELSE ON THE CLIENT. A panel that spelled
 * `authInvitationHours` into a request body would be a second place for the
 * name to drift from the server's.
 *
 * The read answers BOTH the new key and the legacy one, because the panel has
 * to show a firm what is in force — and for a firm that only ever wrote days,
 * what is in force is the converted value, not an empty field.
 */
export const INVITATION_HOURS_KEY = "authInvitationHours";
export const INVITATION_DAYS_KEY = "authInvitationDays";
export const INVITATION_WITNESS_KEY = "authInvitationWitness";

export interface InvitationPolicy {
  /** The window in force, in hours — the new key, or the legacy days × 24. */
  hours: number;
  /** True when the hours row is absent and the number above came from days. */
  fromLegacyDays: boolean;
  witness: string;
}

export const getInvitationPolicy = async (): Promise<InvitationPolicy> => {
  // `Record<string, unknown>` and not `any`: `ratchets.js` may only ever
  // shrink, and the settings map is exactly the shape it is — keys to values
  // nobody here needs to know the type of.
  const all = (await api.get("/settings")) as Record<string, unknown> | null;
  const rawHours = all?.[INVITATION_HOURS_KEY];
  const rawDays = all?.[INVITATION_DAYS_KEY];
  const has = (v: unknown) => v !== undefined && v !== null && v !== "";
  const hours = has(rawHours)
    ? Number(rawHours)
    : has(rawDays)
      ? Number(rawDays) * 24
      : 24;
  return {
    hours: Number.isFinite(hours) ? hours : 24,
    fromLegacyDays: !has(rawHours) && has(rawDays),
    witness: String(all?.[INVITATION_WITNESS_KEY] || "in_app"),
  };
};

export const saveInvitationPolicy = (hours: number, witness: string) =>
  api.put("/settings", {
    [INVITATION_HOURS_KEY]: hours,
    [INVITATION_WITNESS_KEY]: witness,
  });

// ── 81 §6/§7 — WHO CAME IN, AND WHO TRIED ───────────────────────────────────

/**
 * One attempt row, shaped by `server/src/lib/shapes.js`.
 *
 * `location` and `device` arrive WRAPPED rather than flat, and that is the wire
 * carrying §1's caveat instead of each screen remembering it: "`user agent` and
 * `location` are self-reported or inferred. Both are indications and neither is
 * evidence." A component that renders `a.location.country` has the word
 * `inferred` in its hand.
 */
export interface AuthAttemptRow {
  id: number;
  /** Null on `unknown_address` — the one outcome with no subject (§2). */
  collaboratorId: number | null;
  email: string;
  /** §2's six verdicts. Never a password, and never a hash of one. */
  outcome:
    | "unknown_address"
    | "wrong_password"
    | "matches_former_password"
    | "password_ok_second_step_failed"
    | "password_ok_account_blocked"
    | "success";
  /** Why a correct password still did not get in, AS OF that moment. */
  blockedReason: "suspended" | "archived" | "credential_expired" | "kill_switched" | null;
  ip: string;
  location: { country: string | null; city: string | null; kind: "inferred" };
  device: { id: string | null; userAgent: string | null; kind: "self-reported" };
  /** How many attempts this row stands for (§3's collapse). */
  count: number;
  /** On `matches_former_password`: when that password stopped being current. */
  matchedVintage: string | null;
  firstAt: string;
  lastAt: string;
}

/** §7's two facts, plus what the screens need to say them honestly. */
export interface AuthAttemptSummary {
  /** THE SECOND-MOST-RECENT SUCCESS — never the current one. Null on a first-ever sign-in. */
  previousLogin: AuthAttemptRow | null;
  /** Failures since that previous login, summed over collapsed rows. */
  failedSince: number;
  /** True when there is no previous sign-in, so the screen says its own sentence. */
  firstEver: boolean;
  /** Whether the installed database can answer a city at all. */
  location: { hasCity: boolean };
}

export interface AuthAttemptsResponse extends AuthAttemptSummary {
  attempts: AuthAttemptRow[];
  /** §9 — the retention period, travelling WITH the data it describes. */
  retentionDays: number;
  /** The cap this list was taken under, so a screen never reads as complete. */
  limit: number;
}

/** §7 — fetched at boot to render one sentence. Two indexed reads and a sum. */
export const getAuthAttemptSummary = (): Promise<AuthAttemptSummary> =>
  api.get("/auth-attempts/summary") as Promise<AuthAttemptSummary>;

/** §6 — a person's own record. No permission key: it is his own. */
export const getAuthAttempts = (): Promise<AuthAttemptsResponse> =>
  api.get("/auth-attempts") as Promise<AuthAttemptsResponse>;

/** §6 — an administrator reads a colleague's. The read is recorded server-side. */
export const getAuthAttemptsOf = (
  collaboratorId: number,
): Promise<AuthAttemptsResponse & { collaborator: { id: number; email: string | null } }> =>
  api.get(`/auth-attempts/of/${collaboratorId}`) as Promise<
    AuthAttemptsResponse & { collaborator: { id: number; email: string | null } }
  >;

/**
 * §8 — REVOKE EVERYTHING, AS ONE ACT, and end the sessions with it.
 * "Removing rows one at a time is the procedure that misses one, and the
 *  credential missed is the one still open."
 */
export const revokeAllAuthFactors = (
  collaboratorId: number,
): Promise<{ ok: boolean; removed: number; sessionsEnded: boolean }> =>
  api.del(`/auth-factors/of/${collaboratorId}`) as Promise<{
    ok: boolean;
    removed: number;
    sessionsEnded: boolean;
  }>;

/** §8 — what is currently cut off, and the floor the refusals cite. */
export interface KillState {
  firm: { at: string; by: number | null } | null;
  people: number[];
  floor: number;
}
export const getKillState = (): Promise<KillState> =>
  api.get("/collaborators/kill-state") as Promise<KillState>;
export const killPerson = (id: number) => api.post(`/collaborators/${id}/kill`);
export const revivePerson = (id: number) => api.del(`/collaborators/${id}/kill`);
export const killFirm = () => api.post("/collaborators/kill-firm");
export const reviveFirm = () => api.del("/collaborators/kill-firm");
export const regenerateRecoveryCodes = (): Promise<RegenerateResponse> =>
  api.post("/auth-factors/recovery-codes/regenerate") as Promise<RegenerateResponse>;
export function logout() {
  setToken(null);
  setCurrentUser(null);
  setAuthUser(null);
  // 05s — and everything else this application owns. The three above are now
  // redundant (the sweep covers them) and are kept because they are the
  // documented identity contract, and a reader who greps for `lx:authUser`
  // should find it being cleared where it is written.
  clearAppStorage();
}

// Phase 28 (item G) — mint a short-lived single-use SSE stream ticket. Sent
// with the normal Bearer JWT; the returned ticket opens the EventSource.
export const mintStreamTicket = () => api.post("/stream/ticket");

// Entity helpers — matches server/src/routes/*
const crud = (base: any) => ({
  list: () => api.get(base),
  get: (id: any) => api.get(`${base}/${id}`),
  create: (data: any) => api.post(base, data),
  update: (id: any, d: any) => api.put(`${base}/${id}`, d),
  remove: (id: any) => api.del(`${base}/${id}`),
  // Phase 28 (item C/D) — soft archive (reversible) + hard purge. Only the four
  // archivable entities (cases, collective-cases, tasks, agenda) expose these.
  archive: (id: any) => api.post(`${base}/${id}/archive`),
  unarchive: (id: any) => api.post(`${base}/${id}/unarchive`),
});

const clients = crud("/clients");
const cases = crud("/cases");
const collectiveCases = crud("/collective-cases");
const opponents = crud("/opponents");
const opponentLawyers = crud("/opponent-lawyers");
const experts = crud("/experts");
const jurisdictions = crud("/jurisdictions");
const collaborators = crud("/collaborators");
const timeEntries = crud("/time-entries");
// BRIEF 144 §4 — the bulk edit route (PUT /time-entries/bulk). NOT the s()
// engine: the engine diffs to per-row PUTs, and the whole point of the bulk
// route is its per-row gate reporting — a 200 whose body names every skipped
// row ({updated, skipped:[{id, code}]}) instead of an all-or-nothing verdict.
// 145 §1 — the same route now answers with a signed `revert` token: the BEFORE
// values of the fields that changed, on the rows that changed. ABSENT when the
// edit changed nothing, which is what tells the client there is no undo to
// offer. Echoed back UNTOUCHED to /bulk-revert, like the delete's `restore`.
export type TeBulkRevert = { rows: unknown[]; exp: number; sig: string };
export type TeBulkUpdateResult = {
  updated: { id: number; [k: string]: unknown }[];
  skipped: { id: number; code: string }[];
  revert?: TeBulkRevert;
};
export const bulkUpdateTimeEntries = (
  ids: number[],
  patch: Record<string, unknown>,
): Promise<TeBulkUpdateResult> =>
  api.put("/time-entries/bulk", { ids, patch }) as Promise<TeBulkUpdateResult>;
export const bulkRevertTimeEntries = (
  revert: TeBulkRevert,
): Promise<{
  reverted: { id: number; [k: string]: unknown }[];
  skipped: { id: number; code: string }[];
}> =>
  api.post("/time-entries/bulk-revert", revert) as Promise<{
    reverted: { id: number; [k: string]: unknown }[];
    skipped: { id: number; code: string }[];
  }>;
// 144 §5 — hard delete with a true undo. The response's `restore` object is
// the signed verbatim snapshot bundle; Undo echoes it back UNTOUCHED to
// /bulk-restore (the HMAC covers its exact wire form, so any edit refuses).
export type TeBulkDeleteResult = {
  deleted: number[];
  skipped: { id: number; code: string }[];
  restore: { rows: unknown[]; exp: number; sig: string };
};
export const bulkDeleteTimeEntries = (ids: number[]): Promise<TeBulkDeleteResult> =>
  api.post("/time-entries/bulk-delete", { ids }) as Promise<TeBulkDeleteResult>;
export const bulkRestoreTimeEntries = (
  restore: TeBulkDeleteResult["restore"],
): Promise<{
  restored: { id: number; [k: string]: unknown }[];
  skipped: { id: number; code: string }[];
}> =>
  api.post("/time-entries/bulk-restore", restore) as Promise<{
    restored: { id: number; [k: string]: unknown }[];
    skipped: { id: number; code: string }[];
  }>;
// 152 §3 — the SINGLE-row twin. `DELETE /time-entries/:id` now answers with a
// `restore` object of exactly the bulk shape (same HMAC, same 'delete' kind,
// same TTL), and this posts it back UNTOUCHED. Not /bulk-restore: that route
// re-judges through the BULK key, which the lawyer undoing their own delete
// does not hold — see the route's own header.
export type TeDeleteResult = {
  deleted: number;
  restore: TeBulkDeleteResult["restore"];
};
export const restoreTimeEntry = (
  restore: TeBulkDeleteResult["restore"],
): Promise<{
  restored: { id: number; [k: string]: unknown }[];
  skipped: { id: number; code: string }[];
}> =>
  api.post("/time-entries/restore", restore) as Promise<{
    restored: { id: number; [k: string]: unknown }[];
    skipped: { id: number; code: string }[];
  }>;
const agenda = crud("/agenda");
const tasks = crud("/tasks");
const invoices = crud("/invoices");
const books = crud("/books");
const contactPersons = crud("/contact-persons");
const opponentContacts = crud("/opponent-contacts");
const mediators = crud("/mediators");
const arbiters = crud("/arbiters");
const additionalOpponents = crud("/additional-opponents");
const chatMessages = crud("/chat-messages");

export const getClients = clients.list;
export const getClient = clients.get;
export const createClient = clients.create;
export const updateClient = clients.update;
export const deleteClient = clients.remove;
// Phase 36 (D) — client soft archive (reversible). The delete-guard refuses a
// hard delete when the client is linked / its folder is populated and offers
// archive instead.
export const archiveClient = clients.archive;
export const unarchiveClient = clients.unarchive;
/* 168 §1d — the ONE writer of `Client.isProspect`'s clearing. `isProspect` is
   deliberately outside the client PUT's ALLOWED (the boundary `Case.isProspect`
   sits on), so a control that clears it calls this rather than merging a field
   into the row and letting `s()` carry it — the same reason
   `setCaseClientStatus` exists one screen over. Idempotent: a client already
   untagged comes back 200 with its row and nothing is written. Returns the
   whole client, like the archive pair above, so the caller merges one row. */
export const convertClient = (id: number) =>
  api.post(`/clients/${String(id)}/convert`) as Promise<{
    id: number;
    [k: string]: unknown;
  }>;
// Hotfix-B (Part B) — provision SharePoint folders for existing clients whose
// clientRootFolder is NULL. Re-runnable + idempotent. Returns a summary
// { attempted, provisioned, throttled, failed, remaining, skippedDuplicates, ... }.
// The provisioning modal (Hotfix-B correction ITEM 2) passes { ids: [...] } to
// provision exactly the checked selection; the server clamps the id list to
// the first 50. 206C §4.2 — and that is the only body left: the legacy number
// form (`{ limit }`, first-N candidates) went with the server's automatic
// mode, which now answers a body with no id array 400.
export const backfillClientFolders = (opts: { ids: number[] }) =>
  api.post("/clients/backfill-folders", opts);

export const getCases = cases.list;
export const getCase = cases.get;
export const createCase = cases.create;
export const updateCase = cases.update;
export const deleteCase = cases.remove;
// Phase 28 — archive (soft) / purge (hard) / chronology read.
export const archiveCase = cases.archive;
export const unarchiveCase = cases.unarchive;
export const purgeCase = cases.remove;
export const getArchivedCases = () => api.get("/cases?archived=1");
export const getCaseChronicle = (id: any) => api.get(`/cases/${id}/chronicle`);
// BRIEF 140 §2 — the switch (brief 04 unit 7). Convert flips the tag and
// allocates the fresh ref immediately (no Graph call); undo inverts it inside
// the window, carrying the pre-conversion snapshot + the client ids to
// re-tag; finalize is the folder step fired when the window expires
// (rename-or-create + webUrl re-stamp — its response reports what it did).
export const convertCase = (id: number, body: { status?: string }) =>
  api.post(`/cases/${id}/convert`, body);
export const convertCaseUndo = (
  id: number,
  body: {
    status?: string | null;
    pendingDate?: string | null;
    closedDate?: string | null;
    archivedDate?: string | null;
    statusDate?: string | null; // 209 §23
    retagClientIds?: number[];
  },
) => api.post(`/cases/${id}/convert-undo`, body);
export const convertCaseFinalize = (id: number) =>
  api.post(`/cases/${id}/convert-finalize`);
/* 141 §2.2 — the per-matter client status has ONE writer, and it is not the
   case PUT. `status` is deliberately outside the PUT's ALLOWED (the boundary
   `isProspect` sits on), so a control that flips it must call this rather than
   merge a field into the row and let `s()` carry it. `null` clears the status
   and keeps the lifecycle dates — they record what happened. Returns the whole
   shaped case, like the archive/convert routes beside it, so the caller merges
   one row and has the fresh `clLinks` with it. */
export const setCaseClientStatus = (
  caseId: number,
  clientId: number,
  status: string | null,
) => api.put(`/cases/${caseId}/clients/${clientId}/status`, { status });
/* 214 §3 [RULED 52] — the counsel link's state and the contact link's: the same ONE-writer shape as the client's
   (outside the PUT's carry). « Suspended » since `suspendedDate` (YYYY-MM-DD, the window's date), or `null` to
   reactivate. Each answers the whole shaped case; the caller merges `ocuLinks` / `cpLinks` back and nothing else. */
export const setCaseCounselStatus = (
  caseId: number,
  ocuId: number,
  status: string | null,
  suspendedDate?: string,
) => api.put("/cases/" + String(caseId) + "/counsel/" + String(ocuId) + "/status", { status, suspendedDate });
export const setCaseContactStatus = (
  caseId: number,
  cpId: number,
  status: string | null,
  suspendedDate?: string,
) => api.put("/cases/" + String(caseId) + "/contacts/" + String(cpId) + "/status", { status, suspendedDate });
// 58 §2 row 15 — the two matter lists render Latest invoice from ONE column
// declaration, so the two endpoints are asked the same question by name
// rather than by a query string repeated at each call site.
export const getCasesWithLatestInvoice = () => api.get("/cases?withLatestInvoice=1");
// 191B §B.2a — ONE matter, with the list row's three money members
// (latestInvoice · amountDue · recap), served by the same function the list
// read goes through; the row the live-stream handlers REPLACE in `cases` must
// carry them, or the stale recap survives the merge (the reload Laurent needed).
export const getCaseWithLatestInvoice = (id: number) => api.get(`/cases/${String(id)}?withLatestInvoice=1`);

export const getCollectiveCases = collectiveCases.list;
export const getCollectiveCase = collectiveCases.get;
export const createCollectiveCase = collectiveCases.create;
export const updateCollectiveCase = collectiveCases.update;
export const deleteCollectiveCase = collectiveCases.remove;
// Phase 28 — archive (soft) / purge (hard) / chronology read.
export const archiveCollectiveCase = collectiveCases.archive;
export const unarchiveCollectiveCase = collectiveCases.unarchive;
export const purgeCollectiveCase = collectiveCases.remove;
export const getArchivedCollectiveCases = () => api.get("/collective-cases?archived=1");
export const getCollectiveCasesWithLatestInvoice = () =>
  api.get("/collective-cases?withLatestInvoice=1");
// 191B §B.2a — the collective twin of getCaseWithLatestInvoice.
export const getCollectiveCaseWithLatestInvoice = (id: number) =>
  api.get(`/collective-cases/${String(id)}?withLatestInvoice=1`);
export const getCollectiveCaseChronicle = (id: any) => api.get(`/collective-cases/${id}/chronicle`);
// BRIEF 140 §3.2 — the collective half of the switch (brief 04 unit 8). Same
// three steps, same ordering, same meaning as the case trio above; the ONE
// difference is on the server and is named there — a collective conversion
// does NOT untag its members, so there are no client ids to carry back on
// undo (contrast `convertCaseUndo`'s `retagClientIds`).
export const convertCollectiveCase = (id: number, body: { status?: string }) =>
  api.post(`/collective-cases/${id}/convert`, body);
export const convertCollectiveCaseUndo = (
  id: number,
  body: {
    status?: string | null;
    pendingDate?: string | null;
    closedDate?: string | null;
    archivedDate?: string | null;
    statusDate?: string | null; // 209 §23
  },
) => api.post(`/collective-cases/${id}/convert-undo`, body);
export const convertCollectiveCaseFinalize = (id: number) =>
  api.post(`/collective-cases/${id}/convert-finalize`);

export const getOpponents = opponents.list;
export const getOpponent = opponents.get;
export const createOpponent = opponents.create;
export const updateOpponent = opponents.update;
export const deleteOpponent = opponents.remove;

export const getOpponentLawyers = opponentLawyers.list;
export const getOpponentLawyer = opponentLawyers.get;
export const createOpponentLawyer = opponentLawyers.create;
export const updateOpponentLawyer = opponentLawyers.update;
export const deleteOpponentLawyer = opponentLawyers.remove;

export const getExperts = experts.list;
export const getExpert = experts.get;
export const createExpert = experts.create;
export const updateExpert = experts.update;
export const deleteExpert = experts.remove;

export const getJurisdictions = jurisdictions.list;
export const getJurisdiction = jurisdictions.get;
export const createJurisdiction = jurisdictions.create;
export const updateJurisdiction = jurisdictions.update;
export const deleteJurisdiction = jurisdictions.remove;
export const bulkImportJurisdictions = (rows: any) =>
  api.post("/jurisdictions/bulk-import", { rows });

export const getCollaborators = collaborators.list;
export const getCollaborator = collaborators.get;
export const createCollaborator = collaborators.create;
export const updateCollaborator = collaborators.update;
export const deleteCollaborator = collaborators.remove;

export const getTimeEntries = timeEntries.list;
export const getTimeEntry = timeEntries.get;
export const createTimeEntry = timeEntries.create;
export const updateTimeEntry = timeEntries.update;
export const deleteTimeEntry = timeEntries.remove;
// 152 §2 — the fifth archive kind. `crud()` has always minted archive/unarchive
// for every entity; until this brief the time-entry pair had no route to hit.
// `purgeTimeEntry` is deliberately the SAME `DELETE /time-entries/:id` the
// block's own bin calls — ArchiveView reaches it from a second place, with the
// same authority and the same invoiced lock. It is not a second delete.
export const getArchivedTimeEntries = () => api.get("/time-entries?archived=1");
export const archiveTimeEntry = timeEntries.archive;
export const unarchiveTimeEntry = timeEntries.unarchive;
export const purgeTimeEntry = timeEntries.remove;

export const getAgenda = agenda.list;
export const getAgendaEvent = agenda.get;
export const createAgendaEvent = agenda.create;
export const updateAgendaEvent = agenda.update;
export const deleteAgendaEvent = agenda.remove;
// Phase 28 — archive (soft) / purge (hard).
export const archiveAgendaEvent = agenda.archive;
export const unarchiveAgendaEvent = agenda.unarchive;
export const purgeAgendaEvent = agenda.remove;
export const getArchivedAgenda = (caseId?: any, ccId?: any) =>
  api.get(`/agenda?archived=1${caseId ? "&caseId=" + caseId : ""}${ccId ? "&ccId=" + ccId : ""}`);

export const getTasks = tasks.list;
export const getTask = tasks.get;
export const createTask = tasks.create;
export const updateTask = tasks.update;
export const deleteTask = tasks.remove;
// Phase 28 — archive (soft) / purge (hard).
export const archiveTask = tasks.archive;
export const unarchiveTask = tasks.unarchive;
export const purgeTask = tasks.remove;
// Phase 29 round 14 (C82b) — auto-ongoing: fired (fire-and-forget) on every
// timer start/resume bound to a task; the server flips todo → inprogress,
// chronicles with the caller as actor and pushes the row over SSE.
export const autoOngoingTask = (id: any) => api.post(`/tasks/${id}/auto-ongoing`);
export const getArchivedTasks = (caseId?: any, ccId?: any) =>
  api.get(`/tasks?archived=1${caseId ? "&caseId=" + caseId : ""}${ccId ? "&ccId=" + ccId : ""}`);

// Phase M+7 — subtasks. Listing requires a parent qualifier; CRUD calls
// inherit the parent permission gate from the parent Task or AgendaEvent.
export const listSubtasksByTask = (taskId: any) =>
  api.get(`/subtasks?parentTaskId=${encodeURIComponent(taskId)}`);
export const listSubtasksByEvent = (eventId: any) =>
  api.get(`/subtasks?parentEventId=${encodeURIComponent(eventId)}`);
export const createSubtask = (data: any) => api.post("/subtasks", data);
export const updateSubtask = (id: any, d: any) => api.put(`/subtasks/${id}`, d);
export const deleteSubtask = (id: any) => api.del(`/subtasks/${id}`);

// ── 182 A.2 — THE CONTACT TIMELINE (Interaction) ────────────────────────────
// Typed per endpoint (163): the served row is the entity plus what the store's
// `shape()` adds — the author's name and initials, the matter's ref and title
// (for the matter pill on a person's list), the person's name.
export type InteractionRow = Interaction & {
  authorName: string | null;
  authorInitials: string | null;
  caseRef: string | null;
  caseTitle: string | null;
  ccRef: string | null;
  ccTitle: string | null;
  clientName: string | null;
};
/** Exactly one of the three, the routes' rule. */
export interface InteractionOwner {
  caseId?: number | null;
  ccId?: number | null;
  clientId?: number | null;
}
export interface InteractionListOpts {
  includeMatters?: boolean;
  limit?: number;
}
function interactionQuery(owner: InteractionOwner, opts: InteractionListOpts): string {
  const parts: string[] = [];
  if (owner.caseId != null) parts.push("caseId=" + String(owner.caseId));
  else if (owner.ccId != null) parts.push("ccId=" + String(owner.ccId));
  else parts.push("clientId=" + String(owner.clientId ?? ""));
  if (opts.includeMatters) parts.push("includeMatters=1");
  if (opts.limit) parts.push("limit=" + String(opts.limit));
  return parts.join("&");
}
/** Newest first. (186 B.7b — the `before` cursor left with the section's "Load older";
 *  the route still accepts it, unread by any client.) */
export const getInteractions = (owner: InteractionOwner, opts: InteractionListOpts = {}): Promise<InteractionRow[]> =>
  api.get("/interactions?" + interactionQuery(owner, opts)) as Promise<InteractionRow[]>;
/* 183 E.2 — ONE EVENT WHEN THE STORE WRITES. Every mounted Notes section
   (InteractionSection: a case, its collective twin, the client record's card)
   refetches on `lx:reload-interactions`, the `lx:reload-time-entries` shape
   (App.tsx's listener, CaseDocumentsSection's dispatch). Fired here, after the
   write resolves, because this is the one door every writer goes through — the
   section's own composer and the chat's "log as note" alike — so a note logged
   from the sidebar chat appears in the section without a reload. A function
   declaration, not a `<T>(v) =>` arrow: i18n-total parses .ts as TSX;
   `globalThis`, as the other dispatchers of the shape do — `window` is not a
   declared global to this tree's eslint (a node harness has no dispatchEvent,
   hence the guard). */
export const INTERACTIONS_CHANGED_EVENT = "lx:reload-interactions";
function announceInteractionsChanged<T>(v: T): T {
  if (typeof globalThis.dispatchEvent === "function") globalThis.dispatchEvent(new CustomEvent(INTERACTIONS_CHANGED_EVENT));
  return v;
}
export const createInteractionNote = (
  owner: InteractionOwner,
  text: string,
  occurredAt?: string | null,
): Promise<InteractionRow> =>
  api.post("/interactions", { ...owner, text, ...(occurredAt ? { occurredAt } : {}) }).then(announceInteractionsChanged);
/** 186 B.2 — PUT is the archive verb now (a note is never edited after the fact). */
export const archiveInteractionNote = (id: number): Promise<InteractionRow> =>
  (api.put("/interactions/" + String(id), { archived: true }) as Promise<InteractionRow>).then(announceInteractionsChanged);
/** 186 (17) — the way back: the same verb with `false`. B.2a shipped this arm of the
 *  route with no caller on any screen; the archive toast's Undo is its FIRST. */
export const unarchiveInteractionNote = (id: number): Promise<InteractionRow> =>
  (api.put("/interactions/" + String(id), { archived: false }) as Promise<InteractionRow>).then(announceInteractionsChanged);
export const deleteInteractionNote = (id: number) => api.del("/interactions/" + String(id)).then(announceInteractionsChanged);

// ── 190 §C — BENTO PLACEMENTS (where somebody put a card) ───────────────────
// One store for the three Bentos (routes/bentoPlacements.js). `firm` is the
// firm-wide placement (userId null), `mine` the viewer's own; the client reads
// the column through crmDefinitions.mjs bentoColumnOf (mine, else the firm's,
// else the first column). A move writes ONE row and nothing else (190 (3)).
export type BentoItemKind = "task" | "case" | "cc";
export interface BentoPlacements {
  templateCode: string;
  itemKind: BentoItemKind;
  firm: Record<string, string>;
  mine: Record<string, string>;
}
export interface BentoPlacementRow {
  id: number;
  templateCode: string;
  itemKind: BentoItemKind;
  itemId: number;
  column: string;
  userId: number | null;
  updatedAt: string;
}
export const getBentoPlacements = (templateCode: string, itemKind: BentoItemKind): Promise<BentoPlacements> =>
  api.get("/bento/placements?templateCode=" + encodeURIComponent(templateCode) + "&itemKind=" + itemKind) as Promise<BentoPlacements>;
export const putBentoPlacement = (body: { templateCode: string; itemKind: BentoItemKind; itemId: number; column: string; scope: "mine" | "firm" }): Promise<BentoPlacementRow> =>
  api.put("/bento/placements", body) as Promise<BentoPlacementRow>;

// ── 182 B.3 — THE PROSPECT FUNNEL (reports/funnel) ──────────────────────────
// The server's computeFunnel output, typed per endpoint (163); the window is
// echoed back as the two YYYY-MM-DD bounds the route applied.
export interface FunnelRow {
  source: string;
  created: number;
  open: number;
  converted: number;
  lost: number;
  lostBy: Record<string, number>;
  conversionRate: number;
}
export interface FunnelReport {
  from: string;
  to: string;
  rows: FunnelRow[];
  totals: FunnelRow;
}
export const getFunnelReport = (from: string, to: string): Promise<FunnelReport> =>
  api.get("/reports/funnel?from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to)) as Promise<FunnelReport>;

// Phase 29 round 13 (C72) — task document list. Add + archive/restore only;
// the pane never hard-deletes a document.
export const listTaskDocs = (taskId: any) =>
  api.get(`/task-docs?taskId=${encodeURIComponent(taskId)}`);
// 151C §5 — the classify fields the lean stores learned (fileType · date ·
// type · status · note); PATCH is presence-wins server-side, so an absent key
// changes nothing.
export type TaskEventDocFields = {
  fileType?: string | null;
  date?: string | null;
  type?: string | null;
  status?: string | null;
  note?: string | null;
};
export const createTaskDoc = (data: any) => api.post("/task-docs", data);
// 109 §1b — correct label/url in place (the unlocked chip's edit dialog).
export const updateTaskDoc = (
  id: number | string,
  data: { label?: string | null; url: string } & TaskEventDocFields,
) => api.patch(`/task-docs/${id}`, data);
export const archiveTaskDoc = (id: any) => api.post(`/task-docs/${id}/archive`);
export const unarchiveTaskDoc = (id: any) => api.post(`/task-docs/${id}/unarchive`);
// 112 §6 — doc links stop being archivable: the bin DELETEs (Undo re-creates).
export const deleteTaskDoc = (id: number | string) => api.del(`/task-docs/${id}`);

// BRIEF 87 §4 — the agenda-event twins. Same four verbs, same shapes, a different
// parent key: the server side is routes/agendaEventDocs.js, itself C72's mirror.
// Declared beside their siblings rather than in a section of their own, because
// the point of the whole item is that the two are the same thing.
export type AgendaEventDocInput = {
  eventId: number;
  label?: string | null;
  url: string;
} & TaskEventDocFields;
export const listAgendaEventDocs = (eventId: number | string) =>
  api.get(`/agenda-event-docs?eventId=${encodeURIComponent(eventId)}`);
export const createAgendaEventDoc = (data: AgendaEventDocInput) => api.post("/agenda-event-docs", data);
export const updateAgendaEventDoc = (
  id: number | string,
  data: { label?: string | null; url: string } & TaskEventDocFields,
) => api.patch(`/agenda-event-docs/${id}`, data);
export const archiveAgendaEventDoc = (id: number | string) => api.post(`/agenda-event-docs/${id}/archive`);
export const unarchiveAgendaEventDoc = (id: number | string) => api.post(`/agenda-event-docs/${id}/unarchive`);
// 112 §6 — the twin of deleteTaskDoc, for the same reason.
export const deleteAgendaEventDoc = (id: number | string) => api.del(`/agenda-event-docs/${id}`);

// Phase 30 (D6) — the /documents client bindings died with the orphan
// /api/documents route family (no caller existed; case docs live in
// Case.docs JSON, task docs in /task-docs).

export const getInvoices = invoices.list;
export const getInvoice = invoices.get;
export const updateInvoice = invoices.update;
// Phase 35 (C1 / L1+L2) — atomic pro-forma creation: the server allocates the
// number, creates the invoice, and binds the selected entries (invoiceId + mark)
// in one transaction. Payload: { ...invoiceFields, entryIds, number? }.
export const createProForma = (payload: any) => api.post("/invoices/proforma", payload);
// 161 §5 — the matter's SOURCE documents (retainers + cancelled documents
// still holding paid money), served with their live `unconsumed`; never
// derived in the browser.
// 161A §3 — two populations: default = what a bill can TICK (retainers +
// credit documents); "credit" = what a credit can CREDIT (paid retainers +
// activities pro formas, R13).
// 169 R6 — `currency` narrows the offer to the sources in the document's currency.
// 171 §1e — `forInvoiceId` narrows nothing and WIDENS the offer: the balances
// come back measured as if that document had already released its own claims,
// which is what the edit arm ticks against (a fully-consumed source reads 0
// and would otherwise be missing from its own editor).
export const getInvoiceSources = (caseId: number | null, ccId: number | null, population?: "credit", currency?: string | null, forInvoiceId?: number | null) => {
  const cur = currency ? "&currency=" + encodeURIComponent(currency) : "";
  const fori = forInvoiceId ? "&forInvoiceId=" + encodeURIComponent(String(forInvoiceId)) : "";
  return api.get(`/invoices/sources?${caseId ? `caseId=${caseId}` : `ccId=${ccId}`}${population ? `&population=${population}` : ""}${cur}${fori}`);
};
// 161 §9 — the matter's overheads share (R9): the served ex-VAT number the
// recap's OVERHEADS row and gross margin take as an input.
export const getOverheadsShare = (caseId: number | null, ccId: number | null) =>
  api.get(`/financials/overheads-share?${caseId ? `caseId=${caseId}` : `ccId=${ccId}`}`);
// C.2 (BRIEF 04) — server-side ladder compute. Payload: { caseId|ccId,
// entryIds (in the modal's iteration order — float summation is
// order-sensitive), costsRatio, vatRate, commercialDeduction,
// retainerDeduction }. Returns the seven ladder values as raw unrounded
// doubles; display formatting stays client-side.
export const computeProForma = (payload: any) =>
  api.post("/invoices/proforma/compute", payload);

// C.2 — server-side interest compute (the in-case calculator's engine,
// relocated like the ladder above). Payload: { state } — the calculator
// state blob; the server loads the official rates itself. Returns the
// computeAll() shape (perAmount breakdowns + grand totals) as raw unrounded
// doubles; display formatting stays client-side.
export const computeInterest = (payload: any) =>
  api.post("/interest/compute", payload);

// C.2 — server-side retainer-round compute (the collective-case round money,
// relocated like the ladder and the interest engine). Payload: { rounds,
// members: [{ clId, quantum, payments, dueByRound?, terminatedDate? }]
// (clientLinks order; the optional keys only when the link carries them —
// LEGAL-LANE and 141 §3.1), previewQuantum } — all client-held state, posted
// inline. Returns per-member/per-round money
// (due/paid/status/remaining, row totals, amountDue) + grand totals + the
// round-editor preview as raw unrounded doubles; display formatting stays
// client-side.
export const computeRetainerRounds = (payload: any) =>
  api.post("/retainer/compute", payload);

// C.2 — server-side case/CC financial-summary + success-fee compute (the
// "time totals" family, relocated like the ladder, the interest engine and
// the retainer rounds). Payload: { cases?, collectiveCases?, entries?,
// collaborators?, roleRows?, caseModal?, ccModal? } — all client-held state,
// posted inline, reduced to the compute read-set (src/lib/financialsClient).
// Returns per-matter { block, metrics, panel } money + the modal Bruto/Netto
// estimates as raw unrounded doubles; display formatting stays client-side.
export const computeCaseFinancials = (payload: any) =>
  api.post("/financials/compute", payload);

// C.2 — server-side Reports financial-tab aggregates (the six money
// builders behind By Case / By Collaborator / By Period / By Client /
// Collaborators / Invoices, relocated like the case financials). Payload:
// { cases?, collectiveCases?, clients?, collaborators?, entries?,
// invoices?, roleRows?, collabFrom?, collabTo? } — all client-held state,
// posted inline, reduced to the compute read-set (src/lib/financialsClient);
// collabFrom/collabTo carry the By-Collaborator period pills as plain
// strings. Returns the six row arrays as raw unrounded doubles; display
// formatting, search/sort/status filters and the footer sums over the
// filtered subset stay client-side.
export const computeReportsAggregates = (payload: any) =>
  api.post("/financials/reports", payload);

// C.2 — server-side per-entry fee family (TimeEntryTable rows, the
// Dashboard drawer, the TimeExcel export cells, the InvoiceView
// teTotalAmount/markFeeSums — relocated like the case financials). Payload:
// { collaborators?, roleRows?, cases?, collectiveCases?, table?, drawer?,
// export?, invoice? } — all client-held state, posted inline, reduced to
// the compute read-set (src/lib/financialsClient); sections are computed
// iff posted. Raw doubles out (the export section excepted: its rate/amount
// are the browser's final 2-dp/"" XLSX cell values); display formatting
// stays client-side.
export const computeEntryFees = (payload: any) =>
  api.post("/financials/entry-fees", payload);

// C.2 — server-side Costs view tables (per-matter money rows, the
// per-collaborator aggregation with its pro-rata paid allocation, the
// admin-workload rows + insertion-order grand totals — relocated like the
// reports aggregates). Payload: { matters?, entries?, collaborators?,
// roleRows?, period? } — all client-held state, posted inline, reduced to
// the compute read-set (src/lib/financialsClient); `matters` is the
// filter-pill-selected subset in client order, `period` carries the pill +
// six client-derived bound strings. Returns raw unrounded doubles; display
// formatting, the sort and the sort-order-dependent table-1 footer stay
// client-side.
export const computeCostsOverview = (payload: any) =>
  api.post("/financials/costs", payload);

export const getBooks = books.list;
export const getBook = books.get;
export const createBook = books.create;
export const updateBook = books.update;
export const deleteBook = books.remove;
// Phase 16 correction #2 — explicit Borrow / Return actions. The server
// pins borrowedById to req.user.id on borrow; client passes no body.
/* 216 addendum 5 §9 [RULED 80] — the library's import, server side: the file's
   rows and a mode ('preview' writes nothing; 'apply' updates the matched and
   adds the rest; 'replace' also removes the books the file does not list, the
   ones on loan or holding a PDF kept). The answer: the counts, the ambiguous
   rows (listed, never guessed), the unlisted books, and — once applied — what
   was written. */
export interface LibraryImportRow {
  title: string; author: string; publisher: string; year: string; isbn: string; section: string | null;
  code: string; paper: boolean; pdf: boolean; strada: boolean; notes: string;
  /** the sheet's row number (the header is row 1), echoed on an ambiguous row */
  rowNum?: number;
}
export interface LibraryImportPlan {
  mode: "preview" | "apply" | "replace";
  counts: { add: number; update: number; unchanged: number; ambiguous: number; unlisted: number; removable: number; kept: number };
  ambiguous: { index: number; row: number; title: string; author: string; isbn: string; code: string; by: string | null; reason: "books" | "rows" | null; bookIds: number[] }[];
  unlisted: { id: number; title: string | null; author: string | null; removable: boolean; keptFor: "loan" | "pdf" | null }[];
  applied?: { added: number; updated: number; removed: number; unchanged: number; ambiguous: number; kept: number };
}
export const importBooks = (rows: LibraryImportRow[], mode: LibraryImportPlan["mode"]): Promise<LibraryImportPlan> =>
  api.post("/books/import", { rows, mode }) as Promise<LibraryImportPlan>;
export const borrowBook = (id: any) => api.post(`/books/${id}/borrow`);
export const returnBook = (id: any) => api.post(`/books/${id}/return`);

// Phase asset-reservation C3 — server-persistent schedule overrides.
// Cross-user-visible: GET returns every collaborator's overrides in the
// window. The browser-local lx:schedOverride keys are no longer read or
// written by ScheduleView; the server is now the single source of truth.
export const getScheduleOverrides = (from: any, to: any) => {
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const s = qs.toString();
  return api.get(`/schedule-overrides${s ? "?" + s : ""}`);
};
export const putScheduleOverride = (collaboratorId: any, date: any, slots: any) =>
  api.put(`/schedule-overrides/${collaboratorId}/${date}`, { slots });
export const deleteScheduleOverride = (collaboratorId: any, date: any) =>
  api.del(`/schedule-overrides/${collaboratorId}/${date}`);

// Phase 18 — Library 000BIB attachments (catalog admins only — gated by
// lateral.library.edit server-side). pdf-plan computes the deterministic
// filename and reports existence; pdf-upload sends bytes; pdf-detach
// clears pdfFileName + pdfWebUrl without deleting from OneDrive.
export const planBookPdf = (id: any) => api.post(`/books/${id}/pdf-plan`);
export const uploadBookPdf = (id: any, fileBase64: any, contentType: any) =>
  api.post(`/books/${id}/pdf-upload`, { fileBase64, contentType });
export const detachBookPdf = (id: any) => api.post(`/books/${id}/pdf-detach`);
// Phase 18 — copy a 000BIB PDF into a case folder's caseLibraryCopySubfolder.
// Real, independent copy (fetch + re-upload server-side). bookId in body;
// caseId or ccId in path.
export const copyBookToCase = (caseId: any, bookId: any) =>
  api.post(`/cases/${caseId}/library-copy`, { bookId });
export const copyBookToCollectiveCase = (ccId: any, bookId: any) =>
  api.post(`/collective-cases/${ccId}/library-copy`, { bookId });

export const getContactPersons = contactPersons.list;
export const getContactPerson = contactPersons.get;
export const createContactPerson = contactPersons.create;
export const updateContactPerson = contactPersons.update;
export const deleteContactPerson = contactPersons.remove;

export const getOpponentContacts = opponentContacts.list;
export const getOpponentContact = opponentContacts.get;
export const createOpponentContact = opponentContacts.create;
export const updateOpponentContact = opponentContacts.update;
export const deleteOpponentContact = opponentContacts.remove;

export const getMediators = mediators.list;
export const getMediator = mediators.get;
export const createMediator = mediators.create;
export const updateMediator = mediators.update;
export const deleteMediator = mediators.remove;

export const getArbiters = arbiters.list;
export const getArbiter = arbiters.get;
export const createArbiter = arbiters.create;
export const updateArbiter = arbiters.update;
export const deleteArbiter = arbiters.remove;

export const getAdditionalOpponents = additionalOpponents.list;
export const getAdditionalOpponent = additionalOpponents.get;
export const createAdditionalOpponent = additionalOpponents.create;
export const updateAdditionalOpponent = additionalOpponents.update;
export const deleteAdditionalOpponent = additionalOpponents.remove;

// Chat messages — the default list endpoint returns general-chat only;
// use getAllChatMessages() for the boot-loader grouped response.
export const getChatMessagesGeneral = () => api.get("/chat-messages");
export const getChatMessagesByCase = (caseId: any) =>
  api.get(`/chat-messages?caseId=${encodeURIComponent(caseId)}`);
export const getAllChatMessages = () => api.get("/chat-messages?all=1");
export const createChatMessage = chatMessages.create;
// 182 D.1 — "log as note": the message becomes a row of the contact timeline
// (idempotent; a second call answers created:false with the row that exists).
// `target` is read ONLY for a general message that links no matter.
export const logChatMessageAsNote = (
  id: number,
  target?: { caseId?: number | null; ccId?: number | null },
): Promise<{ created: boolean; interaction: InteractionRow }> =>
  (api.post("/chat-messages/" + String(id) + "/log-as-note", target ?? {}) as Promise<{ created: boolean; interaction: InteractionRow }>).then(announceInteractionsChanged); // 183 E.2 — the section refetches
export const updateChatMessage = chatMessages.update;
export const deleteChatMessage = chatMessages.remove;
// Phase 30 Stage-3 (C-2) — the collaborator ids the SSE hub would deliver
// this conversation to (the mention-autocomplete set).
export const getChatRecipients = ({ caseId, ccId }: any = {}) =>
  api.get(
    caseId != null
      ? `/chat-messages/recipients?caseId=${encodeURIComponent(caseId)}`
      : `/chat-messages/recipients?ccId=${encodeURIComponent(ccId)}`,
  );

// Chat read receipts — bespoke shape: GET returns a flattened map
// { "<caseId|gchat>:<userId>": ts } and POST is an upsert by
// (collaboratorId from JWT, caseId). No PUT/DELETE/GET :id — the
// frontend never needs them.
export const getChatReadReceipts = () => api.get("/chat-read-receipts");
export const upsertChatReadReceipt = (payload: any) =>
  api.post("/chat-read-receipts", payload);

// FirmSettings is a singleton on the server (one row, find-or-create). The
// generic crud() helpers don't fit — use bespoke accessors. Allocate calls
// are atomic single-UPDATEs on the server side (see
// server/src/routes/firmSettings.js:66-111) — always use them for formal
// and pro-forma counter increments so concurrent finalizations can't
// double-allocate.
// Phase 11 — model-document generation.
// Template management (Settings). Listing + variables are open to any authed
// user; mutations require lateral.settingsBusiness server-side.
export const getDocTemplates = () => api.get("/doc-templates");
export const getDocTemplateVariables = () =>
  api.get("/doc-templates/variables");
export const createDocTemplate = (data: any) => api.post("/doc-templates", data);
export const updateDocTemplate = (id: any, data: any) =>
  api.put(`/doc-templates/${id}`, data);
export const deleteDocTemplate = (id: any) => api.del(`/doc-templates/${id}`);
// 204 §E — the variant verbs typed (the template's id is a number, a language a
// code, a file a File): the room the third verb below needed in this file's
// eslint ratchet, bought here rather than by a new baseline.
export const deleteDocTemplateVariant = (id: number, lang: string) =>
  api.del(`/doc-templates/${String(id)}/variants/${lang}`);
// Variant upload is multipart — bypass the JSON api() helper so the browser
// sets the multipart boundary itself.
export async function uploadDocTemplateVariant(id: number, language: string, file: File) {
  const fd = new FormData();
  fd.append("language", language);
  fd.append("file", file);
  const res = await fetch(`${API_URL}/doc-templates/${String(id)}/variants`, {
    method: "POST",
    // 174 §1.2 — BOTH credentials, from the one definition: the CSRF header
    // on every mutation, the conditional Bearer, and the device header. No
    // Content-Type — fetch sets the multipart boundary. `credentials` is
    // fetch's own default, stated for the same reason api() states it.
    credentials: "same-origin",
    headers: authHeaders(),
    body: fd,
  });
  const ct = res.headers.get("content-type") || "";
  const body = ct.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    // Same seam, second door: this upload builds its own Error rather than
    // going through api(). brief 38 §3.
    const err: any = new Error(
      srvErrText(body) || (body && body.error) || res.statusText || "Upload failed",
    );
    err.status = res.status;
    err.body = body;
    err.code = (body && body.code) || null;
    throw err;
  }
  return body;
}
/* 204 §E — DOWNLOAD A TEMPLATE'S STORED FILE. The endpoint has existed since
   Phase 11 (`GET /api/doc-templates/:id/variants/:lang/download`, gated on the
   panel's own section key, 404 when the variant is absent); only the button was
   missing. A BLOB response, which api() cannot carry, so this is the third
   bare-fetch door beside the multipart upload above — the same two credentials
   from the one definition (174 §1.2), the refusal's reason read through the
   same srvErr seam (195 §D.2), and the save the way SettingsView's i18n export
   saves: an object URL on an anchor the browser clicks. TYPED, for the same
   reason the email-drop calls below are: the `any` ratchet. */
export async function downloadDocTemplateVariant(id: number, language: string, fileName: string): Promise<void> {
  const res = await fetch(`${API_URL}/doc-templates/${String(id)}/variants/${encodeURIComponent(language)}/download`, {
    credentials: "same-origin",
    headers: authHeaders(),
  });
  if (!res.ok) {
    const body: unknown = await res.json().catch(() => null);
    const env = body && typeof body === "object" ? (body as { error?: unknown }) : null;
    throw new Error(srvErrText(body) ?? (typeof env?.error === "string" ? env.error : res.statusText));
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
/* ── BRIEF 05 STEP 4/5 (via brief 124 §5) — RESOLVE A DROPPED EMAIL ─────────
   TWO CALLS, ONE ROUTE PER MATTER KIND, mirroring the server's twinning. The
   FILE call bypasses the JSON `api()` helper for the reason
   `uploadDocTemplateVariant` above states: the browser must set the multipart
   boundary itself. The id call is ordinary JSON.

   TYPED, though every neighbour in this file is `any`: the `api.ts` ratchet may
   only shrink, and new code does not get to pay for old habits
   (SESSION_RULES). */
export type MatterKind = "ca" | "cc";
export type DroppedEmail = {
  internetMessageId: string | null;
  subject: string | null;
  fromAddress: string | null;
  fromName: string | null;
  sentDate: string | null;
  webLink: string | null;
  wordCount: number | null;
  autoMinutes: number | null;
  activityType: string | null;
  description: string | null;
  alreadyBilled: boolean;
};

/* 126 §10 — `id == null` IS THE DROP THAT NAMES NO MATTER, and it resolves at
   `/api/emails/resolve-drop` (routes/emailDrop.js): neither twin's, because a
   matterless drop is neither twin's case. One place composes the URL, so the
   two callers below need no branch of their own. */
const dropBase = (kind: MatterKind, id: number | string | null) =>
  id == null
    ? `/emails`
    : kind === "cc"
      ? `/collective-cases/${id}/emails`
      : `/cases/${id}/emails`;

export async function resolveDroppedEmailFile(
  kind: MatterKind,
  id: number | string | null,
  file: File,
): Promise<DroppedEmail> {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_URL}${dropBase(kind, id)}/resolve-drop`, {
    method: "POST",
    // 174 §1.2 — BOTH credentials, from the one definition: the CSRF header
    // on every mutation, the conditional Bearer, and the device header. No
    // Content-Type — fetch sets the multipart boundary. `credentials` is
    // fetch's own default, stated for the same reason api() states it.
    credentials: "same-origin",
    headers: authHeaders(),
    body: fd,
  });
  const ct = res.headers.get("content-type") || "";
  const body = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    /* Same seam, second door — `uploadDocTemplateVariant`'s shape (brief 38 §3):
       a multipart call cannot go through `api()`, so it builds the same Error
       with the same `srvErrText` resolution rather than a second spelling. */
    const err = new Error(
      srvErrText(body) || (body && body.error) || res.statusText || "Resolve failed",
    ) as Error & { status?: number; body?: unknown; code?: string | null };
    err.status = res.status;
    err.body = body;
    err.code = (body && body.code) || null;
    throw err;
  }
  return body as DroppedEmail;
}

export const resolveDroppedEmailById = (
  kind: MatterKind,
  id: number | string | null,
  internetMessageId: string,
): Promise<DroppedEmail> =>
  api.post(`${dropBase(kind, id)}/resolve-drop`, { internetMessageId }) as Promise<DroppedEmail>;

/* ── 184 B.4 — THE DROPPED E-MAIL, FILED AS ITS `.eml` UNDER THE ROW IT MADE ─
 *
 * `POST /task-docs/eml` / `POST /agenda-event-docs/eml` (routes/taskDocs.js,
 * routes/agendaEventDocs.js): the parent's id plus EITHER the dropped File
 * (multipart, `resolveDroppedEmailFile`'s shape — the bytes are the drop's)
 * OR the RFC Message-ID as JSON (the `message:` URI drop; the server reads the
 * MIME from the caller's mailbox). Answers the doc row the pane already
 * renders — label, url, `fileType: 'email'`, date. Called by App's `chainGo`
 * AFTER the task/event exists; never a condition of the save. */
export interface DropEml { file: File | null; internetMessageId: string | null }
export interface EmlDocRow {
  id: number;
  label: string | null;
  url: string;
  fileType?: string | null;
  date?: string | null;
  [k: string]: unknown;
}
export async function postEmlDoc(kind: "task" | "event", id: number, dropEml: DropEml): Promise<EmlDocRow> {
  const path = kind === "event" ? "/agenda-event-docs/eml" : "/task-docs/eml";
  const parentKey = kind === "event" ? "eventId" : "taskId";
  if (!dropEml.file) {
    return api.post(path, { [parentKey]: id, internetMessageId: dropEml.internetMessageId }) as Promise<EmlDocRow>;
  }
  const fd = new FormData();
  fd.append(parentKey, String(id));
  if (dropEml.internetMessageId) fd.append("internetMessageId", dropEml.internetMessageId);
  fd.append("file", dropEml.file);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    // 174 §1.2 — both credentials from the one definition; no Content-Type,
    // fetch sets the multipart boundary (the resolve call's own shape above).
    credentials: "same-origin",
    headers: authHeaders(),
    body: fd,
  });
  const ct = res.headers.get("content-type") ?? "";
  const body: unknown = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    /* Same seam, third door — `resolveDroppedEmailFile`'s shape (brief 38 §3):
       a multipart call cannot go through `api()`, so it builds the same Error
       with the same `srvErrText` resolution rather than a second spelling. */
    const env = body && typeof body === "object" ? (body as { error?: unknown; code?: unknown }) : {};
    const err = new Error(
      srvErrText(body) ??
        (typeof env.error === "string" && env.error ? env.error : null) ??
        (res.statusText || "Filing failed"),
    ) as Error & { status?: number; body?: unknown; code?: string | null };
    err.status = res.status;
    err.body = body;
    err.code = typeof env.code === "string" ? env.code : null;
    throw err;
  }
  return body as EmlDocRow;
}

/* ── BRIEF 127 §1 — FILE A DOCUMENT INTO A MATTER'S FOLDER ─────────────────
 *
 * The second half of Laurent's pair ("Browse case files and attach" / "Add to
 * case files and attach"). Server: `routes/matterFiles.js`, one factory, two
 * mounts.
 *
 * NO MATTERLESS FORM, and that is 127 §3 rather than an omission: an email drop
 * can be matterless because it stores a REFERENCE, and a document has bytes
 * that must land somewhere. `id` is therefore required here where `dropBase`
 * above makes it nullable. */
export type MatterFileUpload = {
  ok: true;
  name: string;
  webUrl: string | null;
  id: string | null;
  /** 130-A §N(b)(ii) — WHY the filed name differs from the one sent, or `null`
   *  when it does not. It was `boolean` and covered one of the two causes; the
   *  flag widened into the reason rather than gaining a sibling, because a
   *  boolean plus a reason is two spellings of one fact (128 §2). `null` is
   *  falsy and a word is truthy, so every truthiness reader is unaffected.
   *    `conflict` — a file of that name was already here; §2's walk numbered it
   *    `name`     — characters the drive will not take were replaced
   *    `both`     — both, in that order */
  renamed: "conflict" | "name" | "both" | null;
  /** What the caller sent, so a sentence can name it. `null` when unknown. */
  originalName: string | null;
};

/** The 409 body `matterFiles.name_conflict` carries — §2's dialog reads it. */
export type MatterFileConflict = {
  code: "matterFiles.name_conflict";
  name: string;
  /** What `onConflict: "version"` would produce NOW. A suggestion, never a
   *  reservation — see the route's comment on why the walk is what makes it
   *  safe. */
  suggestion: string;
};

export async function uploadMatterFile(
  kind: MatterKind,
  id: number | string,
  file: File,
  opts: { subPath?: string | null; onConflict?: "fail" | "version"; root?: string | null } = {},
): Promise<MatterFileUpload> {
  const fd = new FormData();
  fd.append("file", file);
  if (opts.subPath) fd.append("subPath", opts.subPath);
  // 216 §6 [RULED 75] — the root the window chose: the matter's folder (absent, as always) or a linked client's
  if (opts.root && opts.root !== "matter") fd.append("root", opts.root);
  if (opts.onConflict) fd.append("onConflict", opts.onConflict);
  const base = kind === "cc" ? `/collective-cases/${id}/files` : `/cases/${id}/files`;
  const res = await fetch(`${API_URL}${base}/upload`, {
    method: "POST",
    // 174 §1.2 — BOTH credentials, from the one definition: the CSRF header
    // on every mutation, the conditional Bearer, and the device header. No
    // Content-Type — fetch sets the multipart boundary. `credentials` is
    // fetch's own default, stated for the same reason api() states it.
    credentials: "same-origin",
    headers: authHeaders(),
    body: fd,
  });
  const ct = res.headers.get("content-type") || "";
  const body = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    /* `resolveDroppedEmailFile`'s shape, for the reason its own comment gives:
       a multipart call cannot go through `api()`, so it builds the same Error
       with the same `srvErrText` resolution rather than a second spelling. */
    const err = new Error(
      srvErrText(body) || (body && body.error) || res.statusText || "Upload failed",
    ) as Error & { status?: number; body?: unknown; code?: string | null };
    err.status = res.status;
    err.body = body;
    err.code = (body && body.code) || null;
    throw err;
  }
  return body as MatterFileUpload;
}

// Generation (per case / collective case).
const docGenBase = (kind: any, id: any) =>
  kind === "cc"
    ? `/collective-cases/${id}/doc-generation`
    : `/cases/${id}/doc-generation`;
export const getDocGenSubfolders = (kind: any, id: any) =>
  api.get(`${docGenBase(kind, id)}/subfolders`);
export const generateDocument = (kind: any, id: any, payload: any) =>
  api.post(docGenBase(kind, id), payload);

export const getFirmSettings = () => api.get("/firm-settings");
export const updateFirmSettings = (patch: any) => api.put("/firm-settings", patch);
// 203-glance-2 §A — the Overheads importer's write WITH the archived categories it restores, one server transaction
export const importOverheads = (body: { overheads: Record<string, Record<string, Record<string, number>>>; restoreCodes: readonly string[] }) =>
  api.put("/firm-settings/overheads", body);
// allocateFormalNumber removed in Phase M+3 — see scope §6.1.
export const allocateProformaNumber = () =>
  api.post("/firm-settings/allocate-proforma-number");

// 111 §5 — the item-link store's client (server: lib/itemLinks.js; the store
// is a matter-scoped JSON column, so every answer carries the matter's full
// itemLinks array for the caller to patch into state).
export type ItemLinkRef = { k: "stage" | "task" | "event"; id: number };
export type ItemLink = {
  id: number;
  a: ItemLinkRef;
  b: ItemLinkRef;
  createdAt?: string;
  createdBy?: number | null;
};
const itemLinkBase = (kind: "ca" | "cc", matterId: number) =>
  `/${kind === "cc" ? "collective-cases" : "cases"}/${matterId}/links`;
export const createItemLink = (
  kind: "ca" | "cc",
  matterId: number,
  a: ItemLinkRef,
  b: ItemLinkRef,
): Promise<{ link: ItemLink; itemLinks: ItemLink[]; existing?: boolean }> =>
  api.post(itemLinkBase(kind, matterId), { a, b }) as Promise<{
    link: ItemLink;
    itemLinks: ItemLink[];
    existing?: boolean;
  }>;
export const deleteItemLink = (
  kind: "ca" | "cc",
  matterId: number,
  linkId: number,
): Promise<{ removed: ItemLink; itemLinks: ItemLink[] }> =>
  api.del(`${itemLinkBase(kind, matterId)}/${linkId}`) as Promise<{
    removed: ItemLink;
    itemLinks: ItemLink[];
  }>;
export const unlinkItemLinks = (
  kind: "ca" | "cc",
  matterId: number,
  item: ItemLinkRef,
): Promise<{ removed: ItemLink[]; itemLinks: ItemLink[] }> =>
  api.post(`${itemLinkBase(kind, matterId)}/unlink-item`, { item }) as Promise<{
    removed: ItemLink[];
    itemLinks: ItemLink[];
  }>;
